import { useState } from "react";
import { Link } from "wouter";
import { Play, Youtube } from "lucide-react";
import { VIDEO_KANITLAR, YOUTUBE_KANAL, videoKapak, videoUrl, type VideoKanit as Video } from "@/lib/video_kanit_data";

/** Tıklanınca iframe yükleyen hafif YouTube kartı (PageSpeed dostu). */
function VideoKart({ v, buyuk = false }: { v: Video; buyuk?: boolean }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className={`border-3 border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex flex-col ${buyuk ? "lg:col-span-2" : ""}`}>
      <div className="relative aspect-video bg-black border-b-3 border-black overflow-hidden">
        {acik ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1&hl=tr`}
            title={v.tamBaslik}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAcik(true)}
            className="group absolute inset-0 w-full h-full cursor-pointer"
            aria-label={`Videoyu oynat: ${v.baslik} — ${v.marka} (${v.sureEtiket})`}
          >
            <img
              src={videoKapak(v.id)}
              alt={`${v.marka} müşteri videosu: ${v.baslik}`}
              width={480}
              height={360}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 bg-[#9EFF00] border-3 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex items-center justify-center group-hover:translate-x-[1px] group-hover:translate-y-[1px] transition-transform">
                <Play className="w-7 h-7 fill-black text-black ml-1" />
              </span>
            </span>
            <span className="absolute bottom-2 right-2 bg-black text-white font-mono text-[11px] font-bold px-1.5 py-0.5">{v.sureEtiket}</span>
          </button>
        )}
      </div>
      <div className="p-5 space-y-2 flex-1 flex flex-col">
        <span className="inline-block w-fit bg-black text-[#9EFF00] border border-black px-2 py-0.5 font-mono font-bold text-[10px] uppercase">{v.marka}</span>
        <h3 className="font-black text-base md:text-lg leading-tight">"{v.baslik}"</h3>
        <p className="text-xs text-neutral-600 font-semibold leading-relaxed flex-1">{v.aciklama}</p>
        <div className="flex flex-wrap gap-3 pt-2 text-xs font-bold">
          <a href={videoUrl(v.id)} target="_blank" rel="noopener" className="inline-flex items-center gap-1 underline hover:text-[#1e7e34]">
            <Youtube className="w-4 h-4" /> YouTube'da izle
          </a>
          {v.link && <Link href={v.link.href} className="underline">{v.link.etiket} →</Link>}
        </div>
      </div>
    </div>
  );
}

/** JSON-LD: müşteri videoları ItemList + VideoObject */
export function videoKanitJsonLd() {
  const hepsi = VIDEO_KANITLAR;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Dijipo müşteri başarı hikayesi videoları",
    "itemListElement": hepsi.map((v, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "VideoObject",
        "name": v.tamBaslik,
        "description": v.aciklama,
        "thumbnailUrl": [videoKapak(v.id)],
        "duration": v.sure,
        "uploadDate": v.yuklenme,
        "contentUrl": videoUrl(v.id),
        "embedUrl": `https://www.youtube-nocookie.com/embed/${v.id}`,
        "publisher": { "@id": "https://dijipo.com/#localbusiness" },
      },
    })),
  };
}

/**
 * "Müşterilerimiz Anlatıyor" bölümü: 4 müşteri videosu (tanıtım videosu kullanıcı talebiyle çıkarıldı).
 */
export default function VideoKanit({ kompakt = false }: { kompakt?: boolean }) {
  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-3 max-w-3xl">
          <span className="inline-block bg-[#9EFF00] text-black border-2 border-black px-3 py-1 font-mono font-bold text-xs uppercase shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">Müşterilerimiz Anlatıyor</span>
          <h2 className="font-black text-3xl md:text-5xl leading-none text-black uppercase">Kendi Ağızlarından Başarı Hikayeleri</h2>
          <p className="text-sm md:text-base text-neutral-600 font-semibold">Bermody, By Selma Özkan, Brunch Plus ve Mira Kids: Dijipo ile çalışma deneyimlerini kamera karşısında anlatıyorlar. Videolar YouTube kanalımızdan, tıklayınca yüklenir.</p>
        </div>
        <a href={YOUTUBE_KANAL} target="_blank" rel="noopener" className="inline-flex items-center gap-2 bg-black text-[#9EFF00] border-2 border-black px-4 py-2 font-bold text-xs shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all shrink-0">
          <Youtube className="w-4 h-4" /> YouTube kanalımız
        </a>
      </div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${kompakt ? "lg:grid-cols-4" : "lg:grid-cols-4"} gap-6`}>
        {VIDEO_KANITLAR.map(v => <VideoKart key={v.id} v={v} />)}
      </div>
    </div>
  );
}
